import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Image,
  Text,
  useMantineColorScheme,
  useMantineTheme,
  SimpleGrid,
  Stack,
} from "@mantine/core";
import { ClockIcon, FootprintsIcon } from "@phosphor-icons/react";
import { PathDetailModal } from "@/components/path-detail-modal";


export default function Atlas() {
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedPath, setSelectedPath] = useState<any>(null);

  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  const handleCardClick = (item: any) => {
    setSelectedPath(item);
    setModalOpened(true);
  };
  const data = [
    {
      title: "Healthy Eating Basics",
      time: "20 min/session",
      waypoints: "3 Waypoints",
      // image: "/assets/healthy_eating.jpg",
      description: "Learn how to build balanced meals with nutrient-rich foods that fuel your body and support long-term health.",
    },
    {
      title: "Daily Hydration Goal",
      time: "15 min/session",
      waypoints: "6 Waypoints",
      // image: "/assets/daily_hydration.jpg",
      description: "Stay energized and focused by meeting your daily water intake with simple reminders and hydration tracking.",
    },
    {
      title: "Beginner Fitness Journey",
      time: "10 min/session",
      waypoints: "5 Waypoints",
      // image: "/assets/beginner_fitness.jpg",
      description: "Start your exercise habit with simple, effective workouts designed for all fitness levels — no gym required.",
    },
  ];

  const path = [
    {
      title: "Better Sleep Routine",
      text: "Improve your sleep quality with bedtime habits, relaxation techniques, and tips for creating a sleep-friendly environment.",
      // image: "/assets/better_sleep_routine.jpg",
      time: "15 min/session",
      waypoints: "3 Waypoints",
    },
    {
      title: "Stress Relief Strategies",
      text: "Reduce anxiety and tension with breathing exercises, meditation, and quick relaxation methods for daily stress management.",
      // image: "/assets/stress_relief_strategy.jpg",
      time: "12 min/session",
      waypoints: "4 Waypoints",
    },
    {
      title: "Screen Time Balance",
      text: "Limit screen time and create healthier tech habits to improve mental focus and reduce eye strain.",
      // image: "/assets/screen_time_balance.jpg",
      time: "10 min/session",
      waypoints: "3 Waypoints",
    },
  ];

  const schedule = [
    {
      title: "Outdoor Wellness Activities",
      text: "Boost your mood and physical health with nature walks, outdoor yoga, or simple activities in fresh air.",
      // image: "/assets/outdoor_wellness.png",
      time: "25 min/session",
      waypoints: "8 Waypoints",
    },
    {
      title: "Sugar Reduction Tips",
      text: "Cut back on added sugars with easy swaps and practical strategies for a healthier diet without feeling deprived.",
      // image: "/assets/sugar_reduction.png",
      time: "10 min/session",
      waypoints: "8 Waypoints",
    },
    {
      title: "Mindful Eating Practice",
      text: "Slow down and enjoy your meals with mindful eating techniques to improve digestion and prevent overeating.",
      // image: "/assets/mindful_eating.png",
      time: "20 min/session",
      waypoints: "2 Waypoints",
    },
  ];

  return (
    <Stack gap="xl" p={{ base: "xs", sm: "md" }}>
      <style>{`
        .lifestyle-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .lifestyle-card:hover {
          background: linear-gradient(180deg,rgb(226, 185, 185) 0%,rgb(149, 156, 163) 100%) !important;
          box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08) !important;
          transform: translateY(-2px);
          border-color: rgba(0, 0, 0, 0.08) !important;
        }
        [data-mantine-color-scheme="dark"] .lifestyle-card:hover {
          background: linear-gradient(180deg, #2a2a2a 0%, #252525 100%) !important;
        }
      `}</style>
      <Box>
        <Group justify="space-between" mb="sm" wrap="wrap">
          <Text
            fw={500}
            size="lg"
            c={
              colorScheme === "dark"
                ? theme.colors.neutral[1]
                : theme.colors.indigo[9]
            }
          >
            Lifestyle Suggestions
          </Text>

          <Button 
            variant="subtle" 
            color={ colorScheme === "dark" ? theme.colors.indigo[6] : theme.colors.indigo[8]}
            size="sm"
          >
            View All
          </Button>
        </Group>

        <Flex 
          gap="md" 
          wrap="nowrap" 
          w="100%" 
          justify="space-between"
          direction={{ base: "column", md: "row" }}
        >
          {data?.map((item, index) => (
            <Card
              key={index}
              withBorder
              shadow="none"
              radius="md"
              p="md"
              h="100%"
              className="lifestyle-card"
              onClick={() => handleCardClick(item)}
              style={{ cursor: "pointer" }}
            >
              <Image
                // src={item.image}
                height={170}
                w="100%"
                fit="cover"
                alt={item.title}
                radius="sm"
              />

              <Text fw={500} size="lg" mt="md">
                {item.title}
              </Text>

              {item.description && (
                <Text 
                  size="sm" 
                  c={colorScheme === "dark" ? theme.colors.neutral[3] : theme.colors.neutral[8]} 
                  mt="xs" 
                  lineClamp={2}
                >
                  {item.description}
                </Text>
              )}

              <Group gap="xs" mt="md" wrap="wrap">
                <Group gap={4}>
                  <ClockIcon size={18} color={colorScheme === "dark" ? "#247ED3" : theme.colors.indigo[9]} />
                  <Text size="sm">{item.time}</Text>
                </Group>
                <Divider
                  orientation="vertical"
                  c={
                    colorScheme === "dark"
                      ? theme.colors.neutral[8]
                      : theme.colors.neutral[8]
                  }
                />
                <Group gap={4}>
                  <FootprintsIcon size={18} color={theme.colors.green[9]} />
                  <Text size="sm">{item.waypoints}</Text>
                </Group>
              </Group>
            </Card>
          ))}
        </Flex>
      </Box>

    <Box mt={"lg"}>
        <SimpleGrid 
          cols={{ base: 1, sm: 2, lg: 3 }} 
          spacing="md"
        >
          {path?.map((pathItem, index) => (
            <Card
              key={index}
              withBorder
              shadow="none"
              radius="md"
              p="md"
              h="100%"
              className="lifestyle-card"
              onClick={() => handleCardClick(pathItem)}
              style={{ cursor: "pointer" }}
            >
              <Image
                // src={pathItem.image}
                height={170}
                w="100%"
                fit="cover"
                alt={pathItem.title}
                radius="sm"
              />

              <Text fw={500} size="lg" mt="md">
                {pathItem.title}
              </Text>

              {pathItem.text && (
                <Text 
                  size="sm" 
                  c={colorScheme === "dark" ? theme.colors.neutral[3] : theme.colors.neutral[8]} 
                  mt="xs" 
                  lineClamp={2}
                >
                  {pathItem.text}
                </Text>
              )}

              <Group gap="xs" mt="md" wrap="wrap">
                <Group gap={4}>
                  <ClockIcon size={18} color={theme.colors.indigo[9]} />
                  <Text size="sm">{pathItem.time}</Text>
                </Group>
                <Divider
                  orientation="vertical"
                  c={
                    colorScheme === "dark"
                      ? theme.colors.neutral[8]
                      : theme.colors.neutral[8]
                  }
                />
                <Group gap={4}>
                  <FootprintsIcon size={18} color={theme.colors.green[9]} />
                  <Text size="sm">{pathItem.waypoints}</Text>
                </Group>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      </Box>

    <Box mt={"lg"}>
        <SimpleGrid 
          cols={{ base: 1, sm: 2, lg: 3 }} 
          spacing="md"
        >
          {schedule?.map((item, index) => (
            <Card
              key={index}
              withBorder
              shadow="none"
              radius="md"
              p="md"
              h="100%"
              className="lifestyle-card"
              onClick={() => handleCardClick(item)}
              style={{ cursor: "pointer" }}
            >
              <Image
                // src={item.image}
                height={170}
                w="100%"
                fit="cover"
                alt={item.title}
                radius="sm"
              />

              <Text fw={500} size="lg" mt="md">
                {item.title}
              </Text>

              {item.text && (
                <Text 
                  size="sm" 
                  c={colorScheme === "dark" ? theme.colors.neutral[3] : theme.colors.neutral[8]} 
                  mt="xs" 
                  lineClamp={2}
                >
                  {item.text}
                </Text>
              )}

              <Group gap="xs" mt="md" wrap="wrap">
                <Group gap={4}>
                  <ClockIcon size={18} color={colorScheme === "dark" ? "#247ED3" : theme.colors.indigo[9]} />
                  <Text size="sm">{item.time}</Text>
                </Group>
                <Divider
                  orientation="vertical"
                  c={
                    colorScheme === "dark"
                      ? theme.colors.neutral[8]
                      : theme.colors.neutral[8]
                  }
                />
                <Group gap={4}>
                  <FootprintsIcon size={18} color={theme.colors.green[9]} />
                  <Text size="sm">{item.waypoints}</Text>
                </Group>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      </Box>

      {/* Path Detail Modal */}
      <PathDetailModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        pathData={selectedPath}
      />
    </Stack>
  );
}
